import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { CalendarDays, BookOpen, Users, Pencil, Archive, UsersRound, MessageSquare } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmptyState } from '@/components/EmptyState';
import { cn } from '@/lib/cn';
import { useOrgBatchDetail, useOrgBatchManagement } from '@/lib/hooks/useOrg';
import { OrgStatusBadge } from '@/features/OrgLayout/primitives/OrgStatusBadge';
import { BATCH_TABS, type BatchTabId } from '@/features/OrgLayout/batchDetail/tabs';
import { BatchOverviewTab } from '@/features/OrgLayout/batchDetail/BatchOverviewTab';
import { BatchStudentsTab } from '@/features/OrgLayout/batchDetail/BatchStudentsTab';
import { BatchAssignmentsTab } from '@/features/OrgLayout/batchDetail/BatchAssignmentsTab';
import { BatchAssessmentsTab } from '@/features/OrgLayout/batchDetail/BatchAssessmentsTab';
import { BatchSessionsTab } from '@/features/OrgLayout/batchDetail/BatchSessionsTab';
import { BatchHackathonsTab } from '@/features/OrgLayout/batchDetail/BatchHackathonsTab';
import { BatchReportsTab } from '@/features/OrgLayout/batchDetail/BatchReportsTab';
import { IntegrityTab } from '@/features/OrgLayout/batchDetail/IntegrityTab';
import { BatchCompanyReadinessTab } from '@/features/OrgLayout/batchDetail/BatchCompanyReadinessTab';
import { BatchFormDialog } from '@/features/OrgLayout/management/BatchFormDialog';
import { ManageTrainersDialog } from '@/features/OrgLayout/management/ManageTrainersDialog';
import { MessageTrainerDialog } from '@/features/OrgLayout/batchDetail/MessageTrainerDialog';

export const Route = createFileRoute('/org/batches/$batchId/')({
  component: BatchDetailPage,
});

function BatchDetailPage() {
  const { batchId } = Route.useParams();
  const { data, isLoading, isError, refetch } = useOrgBatchDetail(batchId);
  const navigate = useNavigate();
  const [tab, setTab] = useState<BatchTabId>('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [trainersOpen, setTrainersOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const mgmt = useOrgBatchManagement(batchId);

  const archive = () => {
    setArchiveOpen(true);
  };

  const confirmArchive = () => {
    mgmt.archiveBatch.mutate(batchId, { onSuccess: () => navigate({ to: '/org/batches' }) });
    setArchiveOpen(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-3 gap-5">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-[24px]" />)}
        </div>
        <Skeleton className="h-64 rounded-[24px]" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <p className="text-[#6b7280]">Couldn&apos;t load this batch.</p>
        <Button className="mt-4" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const { batch, total_topics } = data;
  const activeTab = BATCH_TABS.find((t) => t.id === tab)!;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-[#6b7280]">
        <button type="button" onClick={() => navigate({ to: '/org/batches' })} className="hover:text-[#111827]">Batches</button>
        <span className="text-[#9ca3af]">›</span>
        <span className="font-medium text-[#111827]">{batch.name}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[32px] font-bold leading-tight text-[#111827]">{batch.name}</h1>
        <OrgStatusBadge status={batch.status} />
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => setTrainersOpen(true)} className="inline-flex items-center gap-1.5 rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm font-medium text-[#111827] hover:bg-[#fafafa]">
            <UsersRound className="h-4 w-4" /> Trainers
          </button>
          <button type="button" onClick={() => setMessageOpen(true)} className="relative inline-flex items-center gap-1.5 rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm font-medium text-[#111827] hover:bg-[#fafafa]">
            {batch.pending_trainer_reply && (
              <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary" aria-label="Reply needed" />
            )}
            <MessageSquare className="h-4 w-4" /> Message trainer
          </button>
          <button type="button" onClick={() => setEditOpen(true)} className="inline-flex items-center gap-1.5 rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm font-medium text-[#111827] hover:bg-[#fafafa]">
            <Pencil className="h-4 w-4" /> Edit
          </button>
          <button type="button" onClick={archive} className="inline-flex items-center gap-1.5 rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
            <Archive className="h-4 w-4" /> Archive
          </button>
        </div>
      </div>

      <BatchFormDialog
        mode="edit"
        open={editOpen}
        onOpenChange={setEditOpen}
        batchId={batchId}
        initial={{ name: batch.name, start_date: batch.start_date, end_date: batch.end_date }}
      />
      <ManageTrainersDialog batchId={batchId} open={trainersOpen} onOpenChange={setTrainersOpen} current={batch.trainers} />
      <MessageTrainerDialog
        batchId={batchId}
        trainers={batch.trainers}
        open={messageOpen}
        onOpenChange={setMessageOpen}
      />

      {/* Meta strip (real data only) */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#6b7280]">
        {batch.start_date && (
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-4 w-4 text-[#9ca3af]" /> Started {format(new Date(batch.start_date), 'dd MMM yyyy')}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-[#9ca3af]" /> {total_topics} topic{total_topics === 1 ? '' : 's'}</span>
        <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-[#9ca3af]" /> {batch.student_count} student{batch.student_count === 1 ? '' : 's'}</span>
        {batch.trainers.length > 0 && (
          <span className="inline-flex items-center gap-1.5">
            {batch.trainers.map((t) => t.name).join(', ')}
          </span>
        )}
      </div>

      {/* Tab bar */}
      <div className="border-b border-[#ececec]">
        <div className="flex gap-6 overflow-x-auto">
          {BATCH_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                '-mb-px whitespace-nowrap border-b-2 pb-3 pt-1 text-sm font-medium transition-colors',
                t.id === tab ? 'border-[#ff5a1f] text-[#ff5a1f]' : 'border-transparent text-[#6b7280] hover:text-[#111827]',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab body */}
      {tab === 'overview' && (
        <BatchOverviewTab batchId={batchId} data={data} onViewStudents={() => setTab('students')} />
      )}
      {tab === 'sessions' && <BatchSessionsTab batchId={batchId} />}
      {tab === 'students' && <BatchStudentsTab batchId={batchId} data={data} />}
      {tab === 'assignments' && <BatchAssignmentsTab batchId={batchId} />}
      {tab === 'assessments' && <BatchAssessmentsTab batchId={batchId} />}
      {tab === 'hackathons' && <BatchHackathonsTab batchId={batchId} />}
      {tab === 'company-readiness' && <BatchCompanyReadinessTab batchId={batchId} />}
      {tab === 'integrity' && <IntegrityTab batchId={batchId} />}
      {tab === 'reports' && <BatchReportsTab batchId={batchId} data={data} />}
      {!activeTab.wired && (
        <EmptyState icon={activeTab.icon} title={`${activeTab.label} — coming soon`} description={activeTab.soon} />
      )}

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this batch?</AlertDialogTitle>
            <AlertDialogDescription>It will be hidden from the active list.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmArchive}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
